import React, { useState, useEffect } from 'react';
import { useParams } from "react-router-dom";

import Grid from '@mui/material/Grid2';

import { 
  ModelBase,
} from "@models";
import { 
  GameCard, 
} from "@dndModels";
import { 
  DECK_OF_MANY_THINGS_URLS, 
  DECK_OF_MANY_MORE_THINGS_URLS, 
  DECK_OF_MANY_OLD_THINGS_URLS, 
  MY_DECK_OF_MANY_MORE_THINGS_URLS
} from '../../assets/CardImages';

import API from "@utilities/smart_api";
import CommonUtilities from "@utilities/common_utilities";

import ModelBaseInput from "@dndComponents/ModelBaseInput";
import StringBox from "@components/input/strings/StringBox";
import LoadingBox from "@components/input/LoadingBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import CheckBox from "@components/input/CheckBox";

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

interface Props {
  obj?: ModelBase;
}

export default function GameCardEdit({ obj: param_obj }: Props) {
  const [reloading, setReloading] = useState(false);
  const [obj, setObj] = useState<GameCard>(param_obj instanceof GameCard ? param_obj : api.applyWorkingOn(new GameCard()));
  const [change_time, setChangeTime] = useState(0);
  
  const disabled = api.user === null || !api.online || (!api.user.admin && obj._id !== "" && obj.owner_id !== api.user._id);
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const renderMeanings = (orientation: string) => {
    const meanings = orientation === "Upright" ? obj.upright_meanings : obj.reversed_meanings;
    return (
      <>
        <Grid
          className="header centered"
          size={{
            xs: 12
          }}>{ orientation }</Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <StringBox
            name="Person"
            value={meanings.person}
            disabled={disabled}
            multiline
            onBlur={(changed: string) => {
              const new_obj = obj;
              if (orientation === "Upright") {
                new_obj.upright_meanings.person = changed;
              } else {
                new_obj.reversed_meanings.person = changed;
              }
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <StringBox
            name="Creature or Trap"
            value={meanings.creature_or_trap}
            disabled={disabled}
            multiline
            onBlur={(changed: string) => {
              const new_obj = obj;
              if (orientation === "Upright") {
                new_obj.upright_meanings.creature_or_trap = changed;
              } else {
                new_obj.reversed_meanings.creature_or_trap = changed;
              }
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <StringBox
            name="Place"
            value={meanings.place}
            disabled={disabled}
            multiline
            onBlur={(changed: string) => {
              const new_obj = obj;
              if (orientation === "Upright") {
                new_obj.upright_meanings.place = changed;
              } else {
                new_obj.reversed_meanings.place = changed;
              }
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <StringBox
            name="Treasure"
            value={meanings.treasure}
            disabled={disabled}
            multiline
            onBlur={(changed: string) => {
              const new_obj = obj;
              if (orientation === "Upright") {
                new_obj.upright_meanings.treasure = changed;
              } else {
                new_obj.reversed_meanings.treasure = changed;
              }
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <StringBox
            name="Situation"
            value={meanings.situation}
            disabled={disabled}
            multiline
            onBlur={(changed: string) => {
              const new_obj = obj;
              if (orientation === "Upright") {
                new_obj.upright_meanings.situation = changed;
              } else {
                new_obj.reversed_meanings.situation = changed;
              }
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
      </>
    );
  }
  
  return (
    <ModelBaseInput
      obj={obj}
      disabled={disabled}
      change_time={change_time}
      image_options={
        [
          ...DECK_OF_MANY_THINGS_URLS,
          ...DECK_OF_MANY_MORE_THINGS_URLS,
          ...DECK_OF_MANY_OLD_THINGS_URLS,
          ...MY_DECK_OF_MANY_MORE_THINGS_URLS
        ]
      }
      onChange={(changed: ModelBase) => {
        if (changed instanceof GameCard) {
          const new_obj = obj;
          new_obj.copy(changed);
          setObj(new_obj);
          setChangeTime(common_util.get_time_number());
        }
      }}>
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 12
          }}>
          <SelectObjectBox
            name="Decks"
            data_type="game_deck"
            values={obj.deck_ids}
            multiple
            onChange={(changed: string[]) => {
              const new_obj = obj;
              new_obj.deck_ids = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <StringBox
            name="Effect When Drawn (Overrides Description)"
            value={obj._drawn_effect}
            disabled={disabled}
            multiline
            onBlur={(changed: string) => {
              const new_obj = obj;
              new_obj._drawn_effect = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <SelectObjectBox
            name="Apply Gift When Drawn"
            data_type="gift"
            extra_options={["None"]}
            value={obj.apply_gift_id}
            disabled={disabled}
            onChange={(changed: string) => {
              const new_obj = obj;
              new_obj.apply_gift_id = changed === "None" ? "" : changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 6
          }}>
          <CheckBox
            name="Reshuffle When Drawn"
            value={obj.reshuffle}
            disabled={disabled}
            onChange={(changed: boolean) => {
              const new_obj = obj;
              new_obj.reshuffle = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 6
          }}>
          <CheckBox
            name="Is Primarily Beneficial"
            value={obj.beneficial}
            disabled={disabled}
            onChange={(changed: boolean) => {
              const new_obj = obj;
              new_obj.beneficial = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          className='centered header'
          size={{
            xs: 12
          }}>Adventure Spread Meanings</Grid>
        { renderMeanings("Upright") }
        { renderMeanings("Reversed") }
      </Grid>
    </ModelBaseInput>
  );
}